import { ResolvableLiteral, StateService, Transition, UIRouter } from '@uirouter/angular';

import { IVbNg2StateDeclaration } from 'rev-shared/ts-utils/IVbNg2StateDeclaration';

import { Unauthorized401Component } from 'rev-shared/security/components/Unauthorized401.Component';
import { BootstrapContext } from 'rev-shared/bootstrap/BootstrapContext';
import { IEmbedStartup, EMBED_STARTUP_INJECTION_TOKEN } from './EmbedStartup.Provider';

import loginStyles from './Embed.module.less';
import { PublicVideoPasswordComponent } from 'rev-shared/media/publicVideoPassword/PublicVideoPassword.Component';
import { VideoService } from 'rev-shared/media/Video.Service';
import { LoginRedirectService } from 'rev-shared/security/LoginRedirect.Service';
import { EmbedVideo } from './video/EmbedVideo.Component';
import { HttpErrorResponse } from '@angular/common/http';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { PlaylistService } from 'rev-shared/media/Playlist.Service';
import { IAccountLicense } from 'rev-shared/security/IAccountLicense';
import { AccountLicenseService } from 'rev-shared/security/AccountLicense.Service';
import { decodeQueryString } from 'rev-shared/util/Util.Service';
import { UserLocalIPService } from 'rev-shared/security/UserLocalIP.Service';
import { getCommonSecurityStatesNg2 } from '../shared/security/components/SecurityComponentsState.Config';
import { EmbedLoginComponent } from './login/EmbedLogin.Component';

const EMBED_VIDEO_RESOLVE = 'video';
const PUBLIC_VIDEO_PASSWORD_STATE_NAME: string = 'publicVideoPassword';

const initializationResolve: ResolvableLiteral[] = [
	{
		token: 'EmbedStartupResolve',
		deps: [
			EMBED_STARTUP_INJECTION_TOKEN
		],
		resolveFn: (EmbedStartup: IEmbedStartup) => EmbedStartup.$promise
	},
	{
		token: 'loginStyles',
		resolveFn: () => loginStyles
	},
	{
		token: 'thumbnailUri',
		resolveFn: () => BootstrapContext.thumbnailUri
	}
];

const params401 = {
	reason: {}
};

export function configureEmbedRouter(uiRouter: UIRouter): void {
	uiRouter.trace.enable(/*'RESOLVE',*/ 'TRANSITION');
	uiRouter.urlRouter.otherwise('');
}

export function getEmbedVideoState(stateName: string, includeInitResolve: boolean) {
	return {
		name: stateName,
		component: EmbedVideo,
		url: '',
		params: {
			videoId: null,
			password: null
		},
		allowAllAccess: true,
		redirectTo(transition: Transition) {
			const injector = transition.injector();
			let videoId: string;

			return injector.getAsync('videoId')
				.then(videoIdResult => videoId = videoIdResult)
				.then(() => injector.getAsync(EMBED_VIDEO_RESOLVE)) // from playbackResolve
				.catch((errResponse: HttpErrorResponse) => {
					const reason = errResponse?.error?.reason;

					// if public video with invalid password, redirect to the password input state
					if (reason === 'InvalidPassword') {
						return transition.router.stateService.target(PUBLIC_VIDEO_PASSWORD_STATE_NAME, { videoId }, { location: false });
					}
				});
		},
		resolve: [
			...(includeInitResolve ? initializationResolve : []),

			{
				token: 'accountId',
				deps: [UserContextService],
				resolveFn: (UserContext: UserContextService) => UserContext.getAccount().id
			},

			{
				token: 'BootstrapContext',
				resolveFn: () => BootstrapContext
			},

			{
				token: 'playlistId',
				resolveFn: () => BootstrapContext.playlistId
			},

			{
				token: 'playlist',
				deps: [PlaylistService, 'playlistId'],
				resolveFn(playlistService: PlaylistService, playlistId: string) {
					if (!playlistId) {
						return;
					}

					return playlistService.getPlaylist(playlistId)
						.then((playlist: any) => {
							if (!playlist.videos || !playlist.videos.length) {// no access to any of the videos, so redirect to login
								return Promise.reject({ status: 401 });
							}

							return playlist;
						});
				}
			},

			{
				token: 'videoId',
				deps: ['playlist', Transition],
				resolveFn: (playlist, transition: Transition) => playlist ? playlist.videos[0].id : BootstrapContext.videoId || transition.params().videoId
			},
			{
				token: 'password',
				deps: [Transition],
				resolveFn: (transition: Transition) => transition.params().password
			},

			{
				token: EMBED_VIDEO_RESOLVE,
				deps: [UserLocalIPService, VideoService, 'videoId', 'password', includeInitResolve ? 'EmbedStartupResolve' : undefined].filter(Boolean),
				resolveFn(userLocalIPService: UserLocalIPService, videoService: VideoService, videoId: string, password: string) {
					return userLocalIPService.checkUserLocation()
						.then(() => videoService.getVideoPlaybackEmbed(videoId, password))
						.catch((response: HttpErrorResponse) => {
							console.error('error in video', response);
							return Promise.reject(
								response?.error?.reason === 'EmbedNotAuthorized' ?
									{ ...response, error: { ...response.error, noLoginRedirect: true } } :
									response
							);
						});
				}
			},

			{
				token: 'loadAccountLicense',
				deps: [AccountLicenseService],
				resolveFn: (accountLicense: AccountLicenseService) => accountLicense.reload()
			},

			{
				token: 'accountLicenseCheck',
				deps: [AccountLicenseService, UserContextService, 'loadAccountLicense'],
				resolveFn(accountLicense: AccountLicenseService, UserContext: UserContextService, loadAccountLicense: IAccountLicense) {
					if (!UserContext.isUserAuthenticated() && !accountLicense.mediaViewingAllowed) {
						return Promise.reject({ status: 401 });
					}
				}
			},

			{
				token: 'params',
				resolveFn: decodeQueryString
			}
		],
		bindings: {
			accountLicense: 'loadAccountLicense'
		}
	};

}

export function getPublicVideoPasswordState() {
	return {
		name: PUBLIC_VIDEO_PASSWORD_STATE_NAME,
		component: PublicVideoPasswordComponent,
		allowAllAccess: true,
		resetZoom: true,
		params:{
			videoId: {}
		},
		resolve: [
			{
				token: 'videoId',
				deps: [Transition],
				resolveFn: (transition: Transition) => transition.params().videoId
			},

			{
				token: 'cookieKey',
				deps: ['videoId'],
				resolveFn: (videoId: string) => `${videoId}-auth`
			},

			{
				token: 'videoInfo',
				deps: [VideoService, 'videoId'],
				resolveFn: (videoService: VideoService, videoId: string) => videoService.getVideoBasicInfo(videoId)
			},

			{
				token: 'loginRedirect',
				deps: [LoginRedirectService, StateService],
				resolveFn(loginRedirectService: LoginRedirectService, $state: StateService) {
					const videoRedirect = $state.href('embed', null).substr(1); // remove the leading #

					return () => loginRedirectService.redirectToLogin(videoRedirect);
				}
			},

			{
				token: 'submitRedirect',
				deps: [StateService],
				resolveFn: ($state: StateService) => (password?: string) => $state.go('embed', { password })
			}
		]
	};
}

export const EmbedStates: IVbNg2StateDeclaration[] = [
	...getCommonSecurityStatesNg2(initializationResolve, EmbedLoginComponent as any),

	{
		name: '401',
		url: '/401',
		component: Unauthorized401Component,
		resolve: [
			...initializationResolve,
			{
				token: 'reason',
				deps: [Transition],
				resolveFn: (transition: Transition) => transition.params().reason
			}
		],
		allowAllAccess: true,
		params: params401
	},
	getEmbedVideoState('embed', true),
	getPublicVideoPasswordState()
];
